import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { X, Loader2 } from "lucide-react";
import { EVENT_DATE_LABEL } from "../../config/event";
import { validateRegistration } from "../../lib/validation";
import { createRegistration } from "../../lib/api";

const EMPTY = { first_name: "", email: "", phone: "", sms_consent: false };

const Field = ({ id, label, hint, error, ...input }) => (
  <div>
    <label htmlFor={id} className="mb-1.5 flex items-baseline gap-2 text-[13px] font-semibold text-[#2a2214]">
      {label}
      {hint && <span className="font-normal text-[#7a6a4a]">{hint}</span>}
    </label>
    <input id={id} className="field" aria-invalid={!!error} aria-describedby={error ? `${id}-error` : undefined} data-testid={`registration-${id}-input`} {...input} />
    {error && <p id={`${id}-error`} className="mt-1.5 text-[13px] text-[#b3261e]" data-testid={`registration-${id}-error`}>{error}</p>}
  </div>
);

export const RegistrationModal = ({ open, onClose }) => {
  const navigate = useNavigate();
  const [values, setValues] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const firstRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const opener = document.activeElement;
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const t = setTimeout(() => firstRef.current?.focus(), 30);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
      clearTimeout(t);
      if (opener && document.contains(opener)) opener.focus({ preventScroll: true });
    };
  }, [open, onClose]);

  if (!open) return null;

  const set = (k) => (e) => {
    const v = k === "sms_consent" ? e.target.checked : e.target.value;
    setValues((s) => ({ ...s, [k]: v }));
    if (errors[k]) setErrors((s) => ({ ...s, [k]: undefined }));
  };

  const submit = async (e) => {
    e.preventDefault();
    const errs = validateRegistration(values);
    setErrors(errs);
    setApiError("");
    if (Object.keys(errs).length) return;
    setSubmitting(true);
    try {
      await createRegistration({
        first_name: values.first_name.trim(),
        email: values.email.trim(),
        phone: values.phone.trim(),
        sms_consent: values.sms_consent,
        created_at: new Date().toISOString(),
      });
      setValues(EMPTY);
      onClose();
      navigate("/bridge");
    } catch (err) {
      setApiError("Something went wrong saving your seat. Please check your details and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-end justify-center sm:items-center" data-testid="registration-modal">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} data-testid="registration-modal-backdrop" aria-hidden="true" />
      <div role="dialog" aria-modal="true" aria-labelledby="reg-title" className="pearl-card fade-up relative max-h-[100dvh] w-full overflow-y-auto rounded-t-2xl px-5 pb-7 pt-6 sm:max-w-[460px] sm:rounded-2xl sm:px-8 sm:pb-8 sm:pt-8">
        <button type="button" onClick={onClose} aria-label="Close" className="absolute right-3 top-3 grid h-10 w-10 place-items-center rounded-full text-[#4a3d22] transition-colors hover:bg-black/5" data-testid="registration-modal-close-button">
          <X className="h-5 w-5" />
        </button>
        <p className="font-mono-plex pr-10 text-[11px] uppercase tracking-[0.2em] text-[#8a6416]" data-testid="registration-modal-step">
          Step 1 of 2 · <span className="normal-case tracking-[0.08em]">{EVENT_DATE_LABEL}</span>
        </p>
        <h2 id="reg-title" className="font-display mt-2 text-4xl uppercase leading-none text-[#120e07] sm:text-5xl">Save your seat</h2>
        <form onSubmit={submit} noValidate className="mt-6 space-y-4" data-testid="registration-form">
          <Field id="first_name" label="First name" autoComplete="given-name" value={values.first_name} onChange={set("first_name")} error={errors.first_name} ref={firstRef} />
          <Field id="email" label="Email" type="email" inputMode="email" autoComplete="email" value={values.email} onChange={set("email")} error={errors.email} />
          <Field id="phone" label="Mobile" hint="for the text reminder" type="tel" inputMode="tel" autoComplete="tel-national" value={values.phone} onChange={set("phone")} error={errors.phone} />
          <label className="flex cursor-pointer items-start gap-3 text-[12px] leading-relaxed text-[#4a3f2a]">
            <input type="checkbox" checked={values.sms_consent} onChange={set("sms_consent")} className="mt-0.5 h-5 w-5 shrink-0 accent-[#8a6416]" data-testid="registration-sms-consent-checkbox" />
            <span>I agree to receive text messages from J11 about the class. Msg &amp; data rates may apply. Reply STOP to opt out. Consent is not a condition of purchase.</span>
          </label>
          {apiError && <p role="alert" className="rounded-lg border border-[#b3261e]/30 bg-[#b3261e]/10 px-3 py-2.5 text-[13px] text-[#8c1d18]" data-testid="registration-api-error">{apiError}</p>}
          <button type="submit" disabled={submitting} className="flex w-full items-center justify-center gap-2 rounded-full border border-[#e8b84a]/60 bg-[#0a0a0a] px-6 py-4 text-[15px] font-bold tracking-[0.08em] text-[#f3d27c] transition-[transform,background-color] duration-200 hover:-translate-y-px hover:bg-black disabled:opacity-70" data-testid="registration-submit-button">
            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
            RESERVE MY SEAT →
          </button>
          <p className="text-center text-[12px] text-[#6b5d40]">Free. No credit pull.</p>
        </form>
      </div>
    </div>,
    document.body
  );
};
